import { z } from 'zod';
import { McpServerNameSchema } from '../ids.ts';

const at = z.string();

export const ConsoleEvalStatusSchema = z.enum(['queued', 'running', 'passed', 'failed', 'errored']);
export type ConsoleEvalStatus = z.infer<typeof ConsoleEvalStatusSchema>;

export const ConsoleEvalLastResultSchema = z.object({
  eval_run_id: z.string(),
  passed: z.boolean(),
  errored: z.boolean(),
  at,
});

export const ConsoleEvalCaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  question: z.string(),
  rubric: z.string(),
  reference_notes: z.string().nullable(),
  test_account_id: z.string().nullable(),
  source: z.enum(['manual', 'conversation']),
  source_conversation_id: z.string().nullable(),
  needs_rubric: z.boolean(),
  archived: z.boolean(),
  created_at: at,
  updated_at: at,
  last_result: ConsoleEvalLastResultSchema.nullable(),
});
export type ConsoleEvalCase = z.infer<typeof ConsoleEvalCaseSchema>;

export const ConsoleEvalCasesSchema = z.object({ cases: z.array(ConsoleEvalCaseSchema) });
export type ConsoleEvalCases = z.infer<typeof ConsoleEvalCasesSchema>;

export const ConsoleEvalCaseCreateRequestSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  question: z.string().trim().min(1).max(20_000),
  rubric: z.string().max(10_000).optional(),
  reference_notes: z.string().max(10_000).nullable().optional(),
  test_account_id: z.string().nullable().optional(),
});
export type ConsoleEvalCaseCreateRequest = z.input<typeof ConsoleEvalCaseCreateRequestSchema>;

export const ConsoleEvalCaseUpdateRequestSchema = ConsoleEvalCaseCreateRequestSchema.partial();
export type ConsoleEvalCaseUpdateRequest = z.input<typeof ConsoleEvalCaseUpdateRequestSchema>;

export const ConsoleEvalRunSchema = z.object({
  id: z.string(),
  status: ConsoleEvalStatusSchema,
  model_id: z.string().nullable(),
  case_count: z.number().int(),
  trials_per_case: z.number().int(),
  passed_cases: z.number().int(),
  failed_cases: z.number().int(),
  errored_cases: z.number().int(),
  error: z.string().nullable(),
  created_at: at,
  finished_at: at.nullable(),
});
export type ConsoleEvalRun = z.infer<typeof ConsoleEvalRunSchema>;

export const ConsoleEvalRunsSchema = z.object({ runs: z.array(ConsoleEvalRunSchema) });
export type ConsoleEvalRuns = z.infer<typeof ConsoleEvalRunsSchema>;

export const ConsoleEvalTrialSchema = z.object({
  trial: z.number().int(),
  passed: z.boolean(),
  errored: z.boolean(),
  reasoning: z.string(),
  vendor_tools: z.boolean(),
  user_id: z.string().nullable(),
  conversation_id: z.string().nullable(),
  run_id: z.string().nullable(),
});
export type ConsoleEvalTrial = z.infer<typeof ConsoleEvalTrialSchema>;

export const ConsoleEvalRunDetailSchema = z.object({
  run: ConsoleEvalRunSchema,
  cases: z.array(
    z.object({
      eval_case_id: z.string(),
      name: z.string(),
      question: z.string(),
      passed: z.boolean(),
      errored: z.boolean(),
      trials: z.array(ConsoleEvalTrialSchema),
    }),
  ),
});
export type ConsoleEvalRunDetail = z.infer<typeof ConsoleEvalRunDetailSchema>;

export const ConsoleEvalRunStartRequestSchema = z.strictObject({
  case_ids: z.array(z.string()).min(1).max(500).optional(),
});
export type ConsoleEvalRunStartRequest = z.input<typeof ConsoleEvalRunStartRequestSchema>;

export const ConsoleTestAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  mcp_servers: z.array(z.string()),
  created_at: at,
  updated_at: at,
});
export type ConsoleTestAccount = z.infer<typeof ConsoleTestAccountSchema>;

export const ConsoleTestAccountsSchema = z.object({ accounts: z.array(ConsoleTestAccountSchema) });
export type ConsoleTestAccounts = z.infer<typeof ConsoleTestAccountsSchema>;

export const ConsoleTestAccountRegisterRequestSchema = z.strictObject({
  name: z.string().min(1).max(40),
  servers: z
    .array(
      z.strictObject({
        name: McpServerNameSchema,
        access_token: z.string().min(1).max(16_384),
        expires_at: z.number().int().positive().optional(),
        refresh_token: z.string().min(1).max(16_384).optional(),
      }),
    )
    .max(16),
});
export type ConsoleTestAccountRegisterRequest = z.input<
  typeof ConsoleTestAccountRegisterRequestSchema
>;

export const ConsolePreviewSessionRequestSchema = z.strictObject({
  test_account_id: z.string().nullable(),
});
export type ConsolePreviewSessionRequest = z.input<typeof ConsolePreviewSessionRequestSchema>;

export const ConsolePreviewSessionSchema = z.object({
  client_secret: z.string(),
  expires_at: z.number().int(),
  publishable_key: z.string(),
  embed_origin: z.string(),
  vendor_tools: z.boolean(),
});
export type ConsolePreviewSession = z.infer<typeof ConsolePreviewSessionSchema>;
